import { mercY, mercYToLat } from './geo'
import { LINE_DEFAULT_WIDTH, PROJECTED_WORLD_METRES } from './mapLines'
import type { GeoPt } from './types'

export type LabelAlignment = [number, number]
const project = (p: GeoPt): [number, number] => [
  (p.lon / 360) * PROJECTED_WORLD_METRES,
  (0.5 - mercY(p.lat)) * PROJECTED_WORLD_METRES,
]
const inverse = ([x, y]: [number, number]): GeoPt => ({
  lon: (x / PROJECTED_WORLD_METRES) * 360,
  lat: mercYToLat(0.5 - y / PROJECTED_WORLD_METRES),
})

/** Mobile's middle-vertex/segment anchor and quadrant, in EPSG:3857. */
export function lineLabelLocation(
  points: readonly GeoPt[],
  rotation = 0,
): { center: GeoPt; alignment: LabelAlignment } | null {
  if (points.length < 2) return null
  const mid = Math.floor(points.length / 2)
  const even = points.length % 2 === 0
  const left = points[mid - 1],
    right = points[even ? mid : mid + 1]
  const l = project(left),
    r = project(right)
  const center = even
    ? inverse([(l[0] + r[0]) / 2, (l[1] + r[1]) / 2])
    : points[mid]
  const c = project(center)
  const angle = (p: [number, number]) =>
    (((Math.atan2(p[1] - c[1], p[0] - c[0]) - rotation) % (2 * Math.PI)) +
      2 * Math.PI) %
    (2 * Math.PI)
  const min = Math.min(angle(l), angle(r)),
    max = Math.max(angle(l), angle(r))
  if (min >= Math.PI) return { center, alignment: [0, -1] }
  if (max <= Math.PI) return { center, alignment: [0, 1] }
  if (max - min >= Math.PI - 1e-9)
    return { center, alignment: (min + max) / 2 < Math.PI ? [-1, -1] : [-1, 1] }
  return { center, alignment: (min + max) / 2 < Math.PI ? [1, 1] : [1, -1] }
}

/** Centre of the label after mobile's width clearance and half-label offset. */
export function lineLabelCenter(
  points: readonly GeoPt[],
  width: number | undefined,
  box: { w: number; h: number },
  unitsPerProjectedMetre: number,
): GeoPt | null {
  const location = lineLabelLocation(points)
  if (!location || !(unitsPerProjectedMetre > 0)) return null
  const [x, y] = project(location.center),
    [dx, dy] = location.alignment
  const clearance = width ?? LINE_DEFAULT_WIDTH
  return inverse([
    x + dx * (clearance + box.w / 2 / unitsPerProjectedMetre),
    y - dy * (clearance + box.h / 2 / unitsPerProjectedMetre),
  ])
}

/** Canvas font measurement with a deterministic estimate before fonts load. */
export function nameLabelBox(
  text: string,
  size: number,
  background: boolean,
): { w: number; h: number } {
  let width = [...text].length * size * 0.6
  if (typeof CanvasRenderingContext2D !== 'undefined') {
    const context = document.createElement('canvas').getContext('2d')
    if (context) {
      context.font = `600 ${size}px Geist`
      width = context.measureText(text).width
    }
  }
  return {
    w: width + (background ? 12 : 0),
    h: size * 1.2 + (background ? 4 : 0),
  }
}
